import React from 'react';
import { Row, Col, Form, Badge } from 'react-bootstrap';
import Select from 'react-select';
import { getTypeLabel, getTypeVariant } from './masterRequestConfig';

// Renders a list of field definitions from masterRequestConfig.
// Used by the new-request form (editable) and by the detail / approval views
// (readOnly), so a field only ever has to be described in one place.

const FULL_WIDTH_TYPES = ['textarea'];

// Mapped rather than hardcoded so a phone opens the right keyboard for each field.
const INPUT_TYPES = { email: 'email', tel: 'tel' };
const INPUT_MODES = { email: 'email', tel: 'tel' };

const getError = (field, errors, touched) =>
    touched[field.name] && errors[field.name] ? errors[field.name] : null;

// ---- Compact label/value table for read-only views ----
// One field per row reads as a very long, mostly-empty page once there are
// more than four or five fields. Instead, short fields are packed two per
// row (4 table columns: label, value, label, value); a field with a long
// value - a description, an address, anything past LONG_VALUE_LENGTH - gets
// a row to itself so its text isn't squeezed into a quarter-width cell.
const PAIRS_PER_ROW = 2;
const LONG_VALUE_LENGTH = 40;

const isLongEntry = (item) =>
    item.fullWidth || String(item.display ?? '').length > LONG_VALUE_LENGTH;

export const buildDetailRows = (items, pairsPerRow = PAIRS_PER_ROW) => {
    const rows = [];
    let buffer = [];

    const flushBuffer = () => {
        if (buffer.length === 0) return;
        const group = buffer;
        buffer = [];
        rows.push(
            <tr key={group.map(item => item.label).join('|')}>
                {group.map((item, idx) => {
                    // The last cell in a short row absorbs any unused columns so a
                    // trailing single item doesn't leave a ragged gap.
                    const extra = idx === group.length - 1 ? (pairsPerRow - group.length) * 2 : 0;
                    return (
                        <React.Fragment key={item.label}>
                            <th className="mrq-detail-label">{item.label}</th>
                            <td colSpan={1 + extra} className={item.wrap ? 'text-break' : ''}>
                                {item.display ? item.display : <span className="text-muted">-</span>}
                            </td>
                        </React.Fragment>
                    );
                })}
            </tr>
        );
    };

    items.forEach((item) => {
        if (isLongEntry(item)) {
            flushBuffer();
            rows.push(
                <tr key={item.label}>
                    <th className="mrq-detail-label">{item.label}</th>
                    <td colSpan={pairsPerRow * 2 - 1} className={item.wrap ? 'text-break' : ''}>
                        {item.display ? item.display : <span className="text-muted">-</span>}
                    </td>
                </tr>
            );
            return;
        }

        buffer.push(item);
        if (buffer.length === pairsPerRow) flushBuffer();
    });

    flushBuffer();
    return rows;
};

// Generic version for page-specific rows that aren't driven by field config
// (a request summary strip, the admin's closed/rejected block) - so both
// still read as one consistent table.
export const DetailTable = ({ items, pairsPerRow }) => (
    <div className="table-responsive">
        <table className="table table-bordered mrq-detail-table mb-0">
            <tbody>{buildDetailRows(items, pairsPerRow)}</tbody>
        </table>
    </div>
);

// Colored Type chip, shared by every list/report/detail view so "Item Request" and
// "Customer Request" are always the same color wherever they appear.
export const TypeBadge = ({ reqType }) => (
    <Badge bg={getTypeVariant(reqType)} className="px-3 py-2">
        {getTypeLabel(reqType)}
    </Badge>
);

const ReadOnlyTable = ({ fields, values, options }) => {
    const items = fields.map((field) => {
        let display = values[field.name];

        if (field.type === 'select') {
            const match = (options[field.source] || []).find(o => o.value === display);
            display = match ? match.label : display;
        }

        return {
            label: field.label,
            display,
            fullWidth: field.type === 'textarea',
            wrap: field.type === 'textarea'
        };
    });

    return <DetailTable items={items} />;
};

const EditableField = ({ field, values, errors, touched, options, onChange, onBlur }) => {
    const error = getError(field, errors, touched);
    const value = values[field.name] ?? '';

    return (
        <Form.Group className="mb-3">
            <Form.Label>
                {field.label}{field.required ? ' *' : ''}
            </Form.Label>

            {field.type === 'select' ? (
                <Select
                    inputId={field.name}
                    options={options[field.source] || []}
                    value={(options[field.source] || []).find(o => o.value === value) || null}
                    onChange={(option) => onChange(field.name, option ? option.value : '')}
                    onBlur={() => onBlur(field.name)}
                    placeholder={`Select ${field.label.toLowerCase()}...`}
                    isSearchable
                    isClearable
                    className="react-select-container"
                    classNamePrefix="react-select"
                />
            ) : field.type === 'textarea' ? (
                <Form.Control
                    as="textarea"
                    id={field.name}
                    name={field.name}
                    rows={field.rows || 3}
                    maxLength={field.maxLength}
                    value={value}
                    onChange={(e) => onChange(field.name, e.target.value)}
                    onBlur={() => onBlur(field.name)}
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                    isInvalid={!!error}
                />
            ) : (
                <Form.Control
                    type={INPUT_TYPES[field.type] || 'text'}
                    inputMode={INPUT_MODES[field.type]}
                    autoCapitalize={field.autoCapitalize}
                    id={field.name}
                    name={field.name}
                    maxLength={field.maxLength}
                    value={value}
                    onChange={(e) => onChange(field.name, e.target.value)}
                    onBlur={() => onBlur(field.name)}
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                    isInvalid={!!error}
                />
            )}

            {error && <Form.Text className="text-danger">{error}</Form.Text>}
        </Form.Group>
    );
};

const DynamicFields = ({
    fields = [],
    values = {},
    errors = {},
    touched = {},
    options = {},
    setFieldValue,
    setFieldTouched,
    readOnly = false,
    columns = 2
}) => {
    const colSpan = Math.max(1, Math.round(12 / columns));

    const handleChange = (name, value) => setFieldValue && setFieldValue(name, value);
    const handleBlur = (name) => setFieldTouched && setFieldTouched(name, true);

    if (readOnly) {
        return <ReadOnlyTable fields={fields} values={values} options={options} />;
    }

    return (
        <Row>
            {fields.map((field) => (
                <Col
                    key={field.name}
                    md={FULL_WIDTH_TYPES.includes(field.type) ? 12 : colSpan}
                >
                    <EditableField
                        field={field}
                        values={values}
                        errors={errors}
                        touched={touched}
                        options={options}
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                </Col>
            ))}
        </Row>
    );
};

export default DynamicFields;
